import * as React from "react";
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { useReturnFocus } from "@/lib/returnFocus";
import { cn } from "@/lib/utils";
import { holdDismissWhileBusy } from "@/lib/saveGate";
import { useNavigationHold } from "@/hooks/use-navigation-hold";
import { Button } from "@/components/ui/button";

const DialogBusyContext = React.createContext(false);

/**
 * `busy` holds every way out while a save is in flight (FP-038, lib/saveGate):
 * Escape, an outside click and the X all reach `onOpenChange(false)`, which is
 * dropped, and the X is disabled. Needs a controlled `open`.
 */
function Dialog({
  busy = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & { busy?: boolean }) {
  // Browser Back and leaving the page are ways out too (lib/navigationHold).
  useNavigationHold(busy);
  return (
    <DialogBusyContext.Provider value={busy}>
      <DialogPrimitive.Root
        data-slot="dialog"
        onOpenChange={holdDismissWhileBusy(busy, onOpenChange)}
        {...props}
      />
    </DialogBusyContext.Provider>
  );
}

/** The X. Disabled while the dialog is `busy`. Named "Close" unless the dialog says
 *  otherwise (the payday check-in's "Close, finish later"). */
function DialogCloseButton({ label = "Close" }: { label?: string }) {
  const busy = React.useContext(DialogBusyContext);
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      disabled={busy}
      className="absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-full bg-accent/80 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
    >
      <XIcon className="h-3.5 w-3.5" />
      <span className="sr-only">{label}</span>
    </DialogPrimitive.Close>
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/25 backdrop-blur-sm",
        className,
      )}
      {...props}
    />
  );
}

/**
 * FP-088 (BEN-141, 2026-10-05): every dialog fits the screen and scrolls when its
 * content is taller. Without a viewport cap and a scroller on the card, two shapes
 * failed at 390 at the DEFAULT text size: a 90vh-capped card whose body never
 * scrolled (Add/Edit Debt, Add/Edit Goal: Save and Mark as Paid Off below the
 * fold, unreachable), and an uncapped card taller than the screen, clipped at both
 * ends (the Forecast bucket dialog at 667). Measured on all 43 dialogs:
 * `web-flowpass/30-DIALOG-SWEEP-390.md`.
 *
 * Dialogs that manage their own height keep doing so: a caller's `max-h-*` and
 * `overflow-hidden` win over these defaults through tailwind-merge (pinned by
 * `dialogFitsViewport.test.ts`).
 */
export const DIALOG_CONTENT_BASE =
  "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98] data-[state=closed]:slide-out-to-top-[2%] data-[state=open]:slide-in-from-top-[2%] fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-0 rounded-2xl border border-border/60 p-0 shadow-xl duration-200 outline-none sm:max-w-lg";

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeLabel,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /** The X's accessible name; "Close" when not given. */
  closeLabel?: string;
}) {
  const returnFocus = useReturnFocus(props.onOpenAutoFocus, props.onCloseAutoFocus);
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(DIALOG_CONTENT_BASE, className)}
        {...props}
        {...returnFocus}
      >
        {children}
        {showCloseButton && <DialogCloseButton label={closeLabel} />}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-1.5 px-6 pt-6 pb-0 text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 border-t border-border/60 px-6 py-4 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-[15px] leading-tight font-semibold tracking-tight", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-[13px]", className)}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-body" className={cn("px-6 py-5", className)} {...props} />;
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
